import React from "react";
import "./OutletPage.css";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg     from "../image/gen-bakery-hero.png";
import orderImg    from "../image/gen-bakery-orders.png";
import centralImg  from "../image/gen-bakery-kitchen.png";
import inventoryImg from "../image/gen-bakery-inventory.png";
import dashImg     from "../image/gen-bakery-dashboard.png";
import websiteImg  from "../image/gen-bakery-online.png";
import feedbackImg from "../image/gen-bakery-feedback.png";
import demoImg     from "../image/DemoForm_img.png";

const GRADIENT = "linear-gradient(135deg, #ec4899, #db2777)";
const GLOW     = "rgba(236,72,153,0.3)";

const css = `
  .op-bakery { --op-accent:#ec4899; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(236,72,153,0.1); --op-accent-30:rgba(236,72,153,0.3);
    --op-accent-05:rgba(236,72,153,0.05); }
`;

export default function BakeryOutletPage() {
  return (
    <div className="op-page op-bakery">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Bakery" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Bakery &amp; Sweet Shop POS
          </div>
          <h1 className="op-hero__title">
            Bake more,<br />
            <span className="op-accent-text">worry less</span>
          </h1>
          <p className="op-hero__subtitle">
            Advance orders, central kitchen, delivery tracking and live inventory —
            KadaiPOS handles the business so you can focus on the craft.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost"><Play size={16} /> See It In Action</button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Days</span>
              <span className="op-hero__stat-label">Advance Orders</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Delivery Tracking</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Auto</span>
              <span className="op-hero__stat-label">Kitchen Sync</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Multi</span>
              <span className="op-hero__stat-label">Outlet Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">Core Features</div>
          <h2 className="op-section-title">From oven to doorstep — fully managed</h2>
          <p className="op-section-subtitle">
            Every custom cake order, every delivery run, every ingredient — tracked and managed in one place.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Wide – Advance Ordering */}
          <div className="op-card op-card--wide">
            <img src={orderImg} alt="Advance Orders" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">📅 Advance Orders</div>
              <h3 className="op-card__title">Take orders today, deliver on the right date</h3>
              <p className="op-card__desc">
                Accept custom cake and bakery orders days in advance. The POS schedules production and delivery automatically — no sticky notes, no missed orders.
              </p>
              <ul className="op-card__list">
                <li>Date-wise advance order calendar view</li>
                <li>Customer details, special instructions &amp; deposit collected at booking</li>
                <li>Auto-reminder before delivery / pickup date</li>
                <li>Order status: Received → In Production → Ready → Delivered</li>
              </ul>
            </div>
          </div>

          {/* Narrow accent */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🏭</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Central Kitchen</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                One central kitchen, multiple outlet branches. Transfer products with full stock tracking and outlet-wise pricing.
              </p>
              <ul className="op-card__list">
                <li>Warehouse → outlet product transfer</li>
                <li>Outlet-type pricing (own / franchise / third party)</li>
                <li>Production batch tracking</li>
              </ul>
            </div>
          </div>

          {/* Half – Central Kitchen */}
          <div className="op-card op-card--half">
            <img src={centralImg} alt="Central Kitchen" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">🏭 Kitchen Management</div>
              <h3 className="op-card__title">One kitchen feeding many outlets</h3>
              <p className="op-card__desc">Track exactly how much is produced, transferred, and sold across all your branches — in real time.</p>
            </div>
          </div>

          {/* Half – Dashboard */}
          <div className="op-card op-card--half">
            <img src={dashImg} alt="Dashboard" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📊 Multi-Outlet Dashboard</div>
              <h3 className="op-card__title">All your outlets on one screen</h3>
              <p className="op-card__desc">Compare sales, stock levels and staff performance across all your bakery branches from a single owner dashboard.</p>
            </div>
          </div>

          {/* Full – Inventory */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <img src={inventoryImg} alt="Inventory" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">📦 Ingredient Inventory</div>
              <h3 className="op-card__title">Flour, butter, eggs — never run short before a busy weekend</h3>
              <p className="op-card__desc">
                Each product baked auto-deducts ingredients based on your recipe. Low stock alerts fire before service so your team can reorder in time.
              </p>
              <ul className="op-card__list">
                <li>Recipe-wise auto deduction on every unit produced</li>
                <li>Daily opening &amp; closing stock entries</li>
                <li>Wastage &amp; spoilage log with reason</li>
                <li>Purchase request directly from POS to supplier</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Customer Experience</div>
            <h2 className="op-section-title">More than billing — grow your brand</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={websiteImg} alt="Business Website" />
              <div className="op-deepdive__img-badge">🌐 Online Store</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Your bakery's own online ordering page</h3>
              <p>
                Let customers browse your menu and place advance orders from your own branded website — no third-party platform fee, no commission cuts.
              </p>
              <ul className="op-check-list">
                {[
                  "Branded ordering page with your logo &amp; menu",
                  "Accepts advance bookings with date selection",
                  "Online payment collection at booking",
                  "Order auto-lands on your POS screen",
                  "Delivery zone and slot management"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={feedbackImg} alt="Customer Feedback" />
              <div className="op-deepdive__img-badge">⭐ Feedback</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Collect feedback, act on it before they leave a bad review</h3>
              <p>
                After every order, customers receive an automatic feedback prompt. You see negative ratings instantly — and can act before they go public.
              </p>
              <ul className="op-check-list">
                {[
                  "Auto feedback SMS / WhatsApp after delivery",
                  "Star rating + comment collection",
                  "Negative feedback alert to manager immediately",
                  "Aggregate rating dashboard per outlet",
                  "Respond and resolve inside the POS"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "🚚", title: "Delivery Tracking", desc: "Assign delivery agents and track order status from dispatch to doorstep." },
            { icon: "💳", title: "Advance Payment", desc: "Collect deposits or full payment at booking for custom orders." },
            { icon: "🎂", title: "Custom SKUs", desc: "Create one-off products (custom cakes) with flexible pricing on the spot." },
            { icon: "📣", title: "SMS Marketing", desc: "Blast seasonal offers — Diwali boxes, Valentine's cakes — to your customer list." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Start taking advance orders online from Day 1</h2>
            <p>Our team sets up your bakery POS, online order page, and delivery flow in one session. Book a free demo to see how.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Bakery name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
